import React, { useEffect, useState } from 'react';
import { FaTimes, FaTrashAlt, FaEdit, FaCheck } from 'react-icons/fa';

const overlayBaseStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20000,
};

const modalStyle = {
  backgroundColor: '#0f0f0f',
  color: '#e0e0e0',
  padding: '18px',
  borderRadius: '10px',
  border: '1px solid #2a2a2a',
  width: '92vw',
  maxWidth: '560px',
  maxHeight: '85vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
};

const iconBtnStyle = {
  background: 'transparent',
  border: '1px solid #2a2a2a',
  color: '#e0e0e0',
  borderRadius: '8px',
  padding: '8px 10px',
  cursor: 'pointer',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export default function ManageRunGroupsModal({
  isOpen,
  onClose,
  groups,
  busy,
  onUpdateName,
  onDelete,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [sidebarWidth, setSidebarWidth] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    const measure = () => {
      const el = document.querySelector('.sidebar');
      const w = el && el.getBoundingClientRect ? el.getBoundingClientRect().width : 0;
      setSidebarWidth(Number.isFinite(w) ? w : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setEditingId(null);
      setEditValue('');
    }
  }, [isOpen]);

  const startEdit = (g) => {
    setEditingId(g.id);
    setEditValue(g.name || '');
  };

  const saveEdit = async () => {
    if (editingId == null || busy) return;
    const trimmed = String(editValue || '').trim();
    if (trimmed) {
      const ok = await onUpdateName?.(editingId, trimmed);
      if (ok !== false) {
        setEditingId(null);
        setEditValue('');
      }
    } else {
      setEditingId(null);
      setEditValue('');
    }
  };

  const handleDelete = async (g) => {
    if (busy) return;
    if (!window.confirm(`Delete group "${g.name || 'Unnamed'}"? Runs in this group will be unassigned from it.`)) return;
    const ok = await onDelete?.(g.id);
    if (ok !== false) setEditingId(null);
  };

  if (!isOpen) return null;

  const overlayStyle = {
    ...overlayBaseStyle,
    left: sidebarWidth ? `${sidebarWidth}px` : 0,
    width: sidebarWidth ? `calc(100% - ${sidebarWidth}px)` : '100%',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <div style={{ fontSize: '18px', fontWeight: 800 }}>Manage groups</div>
          <button
            type="button"
            style={iconBtnStyle}
            onClick={onClose}
            disabled={!!busy}
            aria-label="Close"
            title="Close"
          >
            <FaTimes />
          </button>
        </div>

        <p style={{ fontSize: '13px', color: '#888', marginTop: '8px', marginBottom: '12px' }}>
          Edit group names or delete groups. Deleting a group only removes the group; runs are not deleted.
        </p>

        <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
          {groups.length === 0 ? (
            <p style={{ color: '#666', fontSize: '14px' }}>No groups yet. Create one with the + button.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {groups.map((g) => (
                <li
                  key={g.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 0',
                    borderBottom: '1px solid #222',
                  }}
                >
                  {editingId === g.id ? (
                    <>
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit();
                          if (e.key === 'Escape') {
                            setEditingId(null);
                            setEditValue('');
                          }
                        }}
                        autoFocus
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #2a2a2a',
                          background: '#111',
                          color: '#e0e0e0',
                          outline: 'none',
                          fontSize: '14px',
                        }}
                        disabled={!!busy}
                      />
                      <button
                        type="button"
                        style={{ ...iconBtnStyle, color: '#4ade80' }}
                        onClick={saveEdit}
                        disabled={!!busy}
                        title="Save"
                        aria-label="Save"
                      >
                        <FaCheck />
                      </button>
                      <button
                        type="button"
                        style={iconBtnStyle}
                        onClick={() => {
                          setEditingId(null);
                          setEditValue('');
                        }}
                        disabled={!!busy}
                        title="Cancel"
                        aria-label="Cancel"
                      >
                        <FaTimes />
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: '14px' }}>{g.name || 'Unnamed group'}</span>
                      <button
                        type="button"
                        style={iconBtnStyle}
                        onClick={() => startEdit(g)}
                        disabled={!!busy}
                        title="Edit name"
                        aria-label="Edit name"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        style={{ ...iconBtnStyle, color: '#f87171' }}
                        onClick={() => handleDelete(g)}
                        disabled={!!busy}
                        title="Delete group"
                        aria-label="Delete group"
                      >
                        <FaTrashAlt />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
